import { css } from '@emotion/react';
import {
  cloneElement,
  ReactNode,
  Children,
  ReactElement,
  PureComponent,
} from 'react';
import { PositionType } from './Tooltip';
import { InternalArrow, InternalOffset } from './TooltipContainer';
import { calculateContentPosition, calculateContentWidth } from './utils';

const shadowStyles = (move: InternalOffset) => (theme: any) => {
  const shadow = css(theme.tooltip.shadowContainer);

  const styles = [shadow];

  const shadowColor = 'rgba(0,0,0,0.251)';
  const shColAdj = shadowColor
    .slice(0, shadowColor.lastIndexOf(','))
    .replace(/[)]/g, ',');
  const boxShadow = `0 0 15px ${shadowColor}, 0 0 3px ${shColAdj}, 0.1)`;

  styles.push(
    css({
      boxShadow,
      height: `calc(100% - ${Math.max(0, move.down) + Math.max(0, move.up)}px)`,
      width: `calc(100% - ${
        Math.max(0, move.left) + Math.max(0, move.right)
      }px)`,
    })
  );

  return css(...styles);
};

const contentStyles = () => (theme: any) => {
  const textbox = css(theme.tooltip.textbox);
  const defaultStyle = css(theme.tooltip.defaultStyles);

  const styles = [textbox, defaultStyle];

  return css(...styles);
};

const clonedChildStyle = (width: string) => ({
  padding: '12px',
  display: 'block',
  cursor: 'text',
  boxSizing: 'border-box',
  whiteSpace: width === 'auto' ? 'nowrap' : 'normal',
});

export const TOOLTIP_CONTENT_CLASSNAME = '__mapui_tooltip_content';

export interface TooltipContentProps {
  arrow: InternalArrow;
  position: PositionType;
  width?: string;
  offsets: InternalOffset;
  children: ReactNode;
}

export interface TooltipContentState {
  firstH: number;
  lastH: number;
  totH: number;
}

export class TooltipContent extends PureComponent<
  TooltipContentProps,
  TooltipContentState
> {
  state = {
    firstH: null,
    lastH: null,
    totH: null,
  };

  componentDidMount() {
    const heights = Object.keys(this.spanHeights).map(
      (key) => this.spanHeights[key].clientHeight
    );
    const firstH = heights[0];
    const lastH = heights[heights.length - 1];
    const totH = heights.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      0
    );
    this.setState({ totH, firstH, lastH });
  }

  spanHeights = {};

  render() {
    const { arrow, position, width, offsets, children } = this.props;

    const { totH, firstH, lastH } = this.state;

    this.spanHeights = {};

    const clonedChildren = Children.map(
      children,
      (child: ReactElement, index) => {
        let ref = null;
        // eslint-disable-next-line
        ref = (span) => (this.spanHeights[`span${index + 1}`] = span);

        const childProps = {
          ...child.props,
          ref,
          style: clonedChildStyle(width),
        };
        return cloneElement(child, childProps);
      }
    );

    const { left, right, top } = calculateContentPosition(
      arrow,
      position,
      firstH,
      lastH,
      totH
    );

    return (
      <div
        className={TOOLTIP_CONTENT_CLASSNAME}
        style={{
          left,
          right,
          top,
          width: calculateContentWidth(width, offsets),
          borderRadius: '5px',
          position: 'absolute',
          padding: `${offsets.down}px ${offsets.left}px ${offsets.up}px ${offsets.right}px`,
        }}
      >
        <div css={shadowStyles(offsets)} />
        <div css={contentStyles()}>{clonedChildren}</div>
      </div>
    );
  }
}
